import React, { useState, useContext, useMemo } from 'react'
import { Table, Button, Modal, Form, Input, Select, Tooltip, Space, Tag } from 'antd'
import { EditTwoTone, DeleteOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/react-hooks'
import { NavLink } from 'react-router-dom'
import moment from 'moment'
import client from 'apollo'
import get from 'lodash/get'
import { AppContext } from 'AppContext'
import { GET_AUDIT_GROUPS, CREATE_AUDIT_GROUP, UPDATE_AUDIT_GROUP, DELETE_AUDIT_GROUP } from './graphql/auditGroupGraphql'
import { GET_ALL_CLIENT, GET_USERS_FOR_SELECT } from '../Users/graphql/Queries'
import { getName, getFirstName, getLastName, canUserEdit, canUserDelete, canUserAdd, canUserSave } from 'utils/User'
import openNotification from 'utils/Notification'

const { Option } = Select
const { confirm } = Modal

export default function AuditGroupTab() {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasDelete = canUserDelete(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)

  const [skip, setSkip] = useState(0)
  const [limit] = useState(10)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [detailGroup, setDetailGroup] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [form] = Form.useForm()
  const [bulkPastedText, setBulkPastedText] = useState('')
  const [bulkResult, setBulkResult] = useState(null)
  const [showBulkPaste, setShowBulkPaste] = useState(false)

  const { data, loading, refetch } = useQuery(GET_AUDIT_GROUPS, {
    variables: { skip, limit, search },
    fetchPolicy: 'network-only',
  })

  // Fetch all Audit users for mapping dropdown
  const { data: auditUsersData, loading: loadingAuditUsers } = useQuery(GET_USERS_FOR_SELECT, {
    variables: {
      where: { role: 'AUDIT' },
      limit: 1000,
    },
    fetchPolicy: 'network-only',
  })
  const auditUsersList = get(auditUsersData, 'getUsers.users', [])

  // Fetch all client users for dropdown multi-select
  const { data: clientsData, loading: loadingClients } = useQuery(GET_ALL_CLIENT, {
    variables: {
      where: {},
      limit: 1000000,
    },
    fetchPolicy: 'network-only',
  })

  const clientsList = get(clientsData, 'getClientUsers.users', [])

  const handleOpenCreateModal = () => {
    setEditingGroup(null)
    setBulkPastedText('')
    setBulkResult(null)
    setShowBulkPaste(false)
    form.resetFields()
    setShowModal(true)
  }

  const handleOpenEditModal = (record) => {
    setEditingGroup(record)
    setBulkPastedText('')
    setBulkResult(null)
    setShowBulkPaste(false)
    form.setFieldsValue({
      name: record.name,
      auditUserId: record.auditUserId ? String(record.auditUserId) : undefined,
      clientIds: record.clientIds ? record.clientIds.map(id => String(id)) : [],
    })
    setShowModal(true)
  }

  const handleApplyBulkCodes = () => {
    if (!bulkPastedText || !bulkPastedText.trim()) {
      setBulkResult({ error: true, message: 'Please enter at least one user code.' })
      return
    }

    const enteredCodes = bulkPastedText
      .split(/[\n,\t\r\s]+/)
      .map((c) => c.trim().toLowerCase())
      .filter(Boolean)

    if (enteredCodes.length === 0) {
      setBulkResult({ error: true, message: 'No valid user codes found.' })
      return
    }

    // Lookup map by userName (user code) and clientCode
    const codeMap = new Map()
    clientsList.forEach((u) => {
      if (u.userName) codeMap.set(String(u.userName).trim().toLowerCase(), u)
      if (u.clientCode) codeMap.set(String(u.clientCode).trim().toLowerCase(), u)
    })

    const matchedIds = []
    const unmatchedCodes = []

    enteredCodes.forEach((code) => {
      const user = codeMap.get(code)
      if (user) {
        matchedIds.push(String(user.id))
      } else {
        unmatchedCodes.push(code)
      }
    })

    const currentIds = form.getFieldValue('clientIds') || []
    const newCombinedIds = Array.from(new Set([...currentIds, ...matchedIds]))

    form.setFieldsValue({ clientIds: newCombinedIds })

    if (matchedIds.length > 0) {
      openNotification('success', `Added ${matchedIds.length} client(s) to Audit Group.`)
    }

    setBulkResult({
      error: unmatchedCodes.length > 0 && matchedIds.length === 0,
      message: `Matched and added ${matchedIds.length} of ${enteredCodes.length} user code(s).`,
      unmatched: unmatchedCodes,
      matchedCount: matchedIds.length,
    })
  }

  const handleOpenDetailModal = (record) => {
    setDetailGroup(record)
    setShowDetailModal(true)
  }

  const handleDelete = (id) => {
    confirm({
      title: 'Are you sure you want to delete this Audit Group?',
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      onOk() {
        client
          .mutate({
            mutation: DELETE_AUDIT_GROUP,
            variables: { id },
          })
          .then(() => {
            openNotification('success', 'Audit Group Deleted Successfully')
            refetch()
          })
          .catch((err) => console.error(err))
      },
    })
  }

  const handleFormSubmit = (values) => {
    const payload = {
      name: values.name,
      auditUserId: values.auditUserId ? String(values.auditUserId) : null,
      clientIds: values.clientIds || [],
    }

    const handleError = (err) => {
      const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'An error occurred'
      openNotification('error', msg)
    }

    if (editingGroup) {
      client
        .mutate({
          mutation: UPDATE_AUDIT_GROUP,
          variables: { data: { id: editingGroup.id, ...payload } },
        })
        .then(() => {
          openNotification('success', 'Audit Group Updated Successfully')
          setShowModal(false)
          form.resetFields()
          refetch()
        })
        .catch(handleError)
    } else {
      client
        .mutate({
          mutation: CREATE_AUDIT_GROUP,
          variables: { data: payload },
        })
        .then(() => {
          openNotification('success', 'Audit Group Created Successfully')
          setShowModal(false)
          form.resetFields()
          refetch()
        })
        .catch(handleError)
    }
  }

  const columns = [
    {
      title: 'Audit Group Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span
          onClick={() => handleOpenDetailModal(record)}
          style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Assigned Audit User',
      dataIndex: 'auditUser',
      key: 'auditUser',
      render: (auditUser, record) => {
        if (auditUser) {
          return <Tag color="geekblue">{getName(auditUser) || auditUser.userName || auditUser.email}</Tag>
        }
        return <Tag color="default">Unassigned</Tag>
      },
    },
    {
      title: 'Mapped Clients Count',
      dataIndex: 'clientIds',
      key: 'clientCount',
      render: (clientIds, record) => (
        <span
          onClick={() => handleOpenDetailModal(record)}
          style={{ fontWeight: 'bold', cursor: 'pointer', color: '#52c41a' }}
        >
          {Array.isArray(clientIds) ? clientIds.length : 0} Clients (View Details)
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? moment(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {hasEdit && (
            <Tooltip title="Edit Audit Group">
              <EditTwoTone onClick={() => handleOpenEditModal(record)} />
            </Tooltip>
          )}
          {hasDelete && (
            <Tooltip title="Delete Audit Group">
              <DeleteOutlined onClick={() => handleDelete(record.id)} style={{ color: 'red' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]

  const clientPlanMap = useMemo(() => {
    const map = new Map()
    clientsList.forEach((c) => {
      if (c && c.id) {
        map.set(String(c.id), c.plan?.name || '')
      }
    })
    return map
  }, [clientsList])

  const clientDetailColumns = [
    {
      title: 'Client Code',
      dataIndex: 'clientCode',
      key: 'clientCode',
      render: (code, record) => (
        <NavLink to={`/clients/${record.id}`}>
          <Tag color="blue">{code || record.userName || record.id}</Tag>
        </NavLink>
      ),
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (_, record) => getFirstName(record),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      render: (_, record) => getLastName(record),
    },
    {
      title: 'User Code',
      dataIndex: 'userName',
      key: 'userName',
      render: (userName, record) => record?.userName || userName || '-',
    },
    {
      title: 'Plan Name',
      dataIndex: 'plan',
      key: 'planName',
      render: (_, record) => {
        return (
          record?.plan?.name ||
          clientPlanMap.get(String(record.id)) ||
          record?.strategyCode ||
          '-'
        )
      },
    },
  ]

  const auditGroups = get(data, 'getAuditGroups.groups', [])
  const totalCount = get(data, 'getAuditGroups.count', 0)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Input
          placeholder="Search Audit Group..."
          prefix={<SearchOutlined />}
          style={{ width: 280 }}
          onChange={(e) => setSearch(e.target.value)}
        />
        {hasAdd && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
            Create Audit Group
          </Button>
        )}
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={auditGroups.map((g) => ({ ...g, key: g.id }))}
        pagination={{
          total: totalCount,
          pageSize: limit,
          onChange: (page) => setSkip((page - 1) * limit),
        }}
      />

      {/* Audit Group Detail Modal showing mapped clients */}
      {showDetailModal && (
        <Modal
          visible={showDetailModal}
          width={800}
          title={`Audit Group Mapped Clients: ${detailGroup?.name}`}
          onCancel={() => setShowDetailModal(false)}
          footer={[
            <Button key="close" type="primary" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>,
          ]}
        >
          <Table
            columns={clientDetailColumns}
            dataSource={(detailGroup?.clients || []).map((c) => ({ ...c, key: c.id }))}
            pagination={{ pageSize: 5 }}
            size="small"
          />
        </Modal>
      )}

      {showModal && (
        <Modal
          visible={showModal}
          maskClosable={false}
          title={editingGroup ? 'Edit Audit Group' : 'Create Audit Group'}
          onCancel={() => setShowModal(false)}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
            <Form.Item
              name="auditUserId"
              label="Assign to Audit User"
              rules={[{ required: true, message: 'Please select an Audit User' }]}
              help="Clients mapped in this group will strictly belong to this Audit User."
            >
              <Select
                showSearch
                loading={loadingAuditUsers}
                placeholder="Select Audit User..."
                optionFilterProp="title"
                filterOption={true}
                style={{ width: '100%' }}
                onChange={(val) => {
                  if (!editingGroup && !form.getFieldValue('name')) {
                    const found = auditUsersList.find((u) => String(u.id) === String(val))
                    if (found) {
                      form.setFieldsValue({ name: `Audit - ${getName(found) || found.userName}` })
                    }
                  }
                }}
              >
                {auditUsersList.map((user) => (
                  <Option key={user.id} value={String(user.id)} title={getName(user) || user.userName}>
                    {getName(user)} ({user.userName || user.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="name"
              label="Audit Group Name"
              rules={[{ required: true, message: 'Please enter Audit Group Name' }]}
            >
              <Input placeholder="Enter Audit Group Name (e.g. Audit Group 1)" />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.85)' }}>
                Select & Map Clients
                <span style={{ fontWeight: 'normal', color: '#8c8c8c', marginLeft: 6 }}>
                  ({(form.getFieldValue('clientIds') || []).length} Selected)
                </span>
              </span>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, fontWeight: 500 }}
                onClick={() => setShowBulkPaste(!showBulkPaste)}
              >
                {showBulkPaste ? '✕ Hide Bulk Paste' : '📋 Bulk Paste User Codes (200+)'}
              </Button>
            </div>

            {showBulkPaste && (
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                  Paste user codes separated by commas, spaces, or new lines (e.g. copied from Excel):
                </div>
                <Input.TextArea
                  rows={4}
                  placeholder="e.g. USR001, USR002&#10;USR003&#10;USR004..."
                  value={bulkPastedText}
                  onChange={(e) => setBulkPastedText(e.target.value)}
                />
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleApplyBulkCodes}
                    >
                      Match & Add to Group
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setBulkPastedText('')
                        setBulkResult(null)
                      }}
                    >
                      Clear
                    </Button>
                  </Space>
                  {bulkResult && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: bulkResult.error ? '#f5222d' : '#52c41a',
                      }}
                    >
                      {bulkResult.message}
                    </span>
                  )}
                </div>
                {bulkResult?.unmatched?.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#f5222d' }}>
                    Not found ({bulkResult.unmatched.length}):{' '}
                    {bulkResult.unmatched.slice(0, 8).join(', ')}
                    {bulkResult.unmatched.length > 8 ? ` and ${bulkResult.unmatched.length - 8} more...` : ''}
                  </div>
                )}
              </div>
            )}

            <Form.Item
              name="clientIds"
              help="Search and select clients to allow Audit Role users to view them."
            >
              <Select
                mode="multiple"
                showSearch
                loading={loadingClients}
                placeholder="Search by user code or client name..."
                optionFilterProp="title"
                filterOption={true}
                style={{ width: '100%' }}
              >
                {clientsList.map((user) => {
                  const userCode = user.userName || user.clientCode || ''
                  const title = `${userCode} - ${getName(user)}${
                    user?.plan?.name ? ` - ${user.plan.name}` : ''
                  }`
                  return (
                    <Option key={user.id} value={String(user.id)} title={title}>
                      {userCode ? `${userCode} - ` : ''}{getName(user)}
                      {user?.plan?.name ? ` - ${user.plan.name}` : ''}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>

            <div className="footer-btn-div" style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                className="footer-cancel-btn"
                onClick={() => {
                  form.resetFields()
                  setShowModal(false)
                }}
              >
                Cancel
              </Button>
              <Button
                className="footer-cancel-btn"
                onClick={() => {
                  form.setFieldsValue({ clientIds: [] })
                }}
              >
                Remove All
              </Button>
              <Button
                className="footer-submit-btn"
                type="primary"
                htmlType="submit"
                disabled={editingGroup ? !(hasEdit && hasSave) : !(hasAdd && hasSave)}
              >
                {editingGroup ? 'Update Group' : 'Add Group'}
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </div>
  )
}
